// CJS export on purpose: the plain-node unit suite requires this file directly.

const HIDDEN_FIELDS = ["isHydrated", "status"];

const HIDDEN_FIELDS_BY_ENTITY = {
  QuizzesPolls: {
    Configuration: ["newQuestionnaireName"],
    Questionnaire: ["newName", "postProcessingSteps"],
    Question: ["newOptionLabel", "isCollapsed", "name", "answerType", "correctAnswer", "rank"],
    QuestionOption: ["rank"],
    Section: ["isCollapsed", "rank"],
    PostProcessingStep: ["rank"],
    Answer: ["gradedManually"],
  },
  MoneyCoachVince: {
    StabilityCheck: ["answers", "sourceSubmissionId", "sourceQuestionnaireId"],
    StabilityScore: ["answers", "statements", "sourceSubmissionId", "sourceQuestionnaireId"],
  },
};

const INPUT_ONLY_FIELDS_BY_APP = {
  QuizzesPolls: [
    "draftValue",
    "draftNumber",
    "draftOption",
    "draftFileUrl",
    "draftFileName",
    "draftFileType",
  ],
};

const FIELD_LABELS_BY_ENTITY = {
  QuestionOption: {
    value: "Match Value",
    label: "Choice Text",
  },
  Answer: {
    value: "Response",
    valueLabel: "Response Text",
    valueChecked: "Checked",
    valueImage: "Image",
    questionKey: "Answer Key",
    isAnswered: "Answered",
    grade: "Result",
  },
};

// Entities that exist only so the app can run: the install's own config row,
const HIDDEN_CONTEXT_ENTITIES_BY_APP = {
  QuizzesPolls: ["Configuration", "Participant", "PostProcessingStep", "PostProcessingParamMapping"],
};

const UPLOAD_FIELDS_BY_ENTITY = {
  QuizzesPolls: {
    Answer: ["draftFileUrl"],
  },
};

const RESET_DAY_LINK_SLOTS = {
  link1Url: "link1Label",
  link2Url: "link2Label",
  link3Url: "link3Label",
};

const LINK_FIELDS_BY_ENTITY = {
  MoneyCoachVince: {
    SevenDayResetDay3: RESET_DAY_LINK_SLOTS,
    SevenDayResetDay4: RESET_DAY_LINK_SLOTS,
    SevenDayResetDay5: RESET_DAY_LINK_SLOTS,
    SevenDayResetDay6: RESET_DAY_LINK_SLOTS,
    MoneyPlan: RESET_DAY_LINK_SLOTS,
  },
};

const COMMIT_ONLY_ACTIONS = ["save", "saveNumber", "selectOption", "addFile"];

const DATE_FIELD_LABELS = {
  timestampCreated: "Created Date",
  timestampUpdated: "Updated Date",
};

class AppPaletteFieldPolicy {

  static HIDDEN_FIELDS = HIDDEN_FIELDS;

  static isCommitOnlyAction(actionKey) {
    return COMMIT_ONLY_ACTIONS.includes(actionKey);
  }

  // File Upload Container (carrying the field) instead of a text box.
  static isUploadField(appName, entityName, fieldKey) {
    return (UPLOAD_FIELDS_BY_ENTITY[appName]?.[entityName] ?? []).includes(fieldKey);
  }

  static linkLabelFieldFor(appName, entityType, fieldKey) {
    const entityName = String(entityType ?? "").split(".").at(-1);
    return LINK_FIELDS_BY_ENTITY[appName]?.[entityName]?.[fieldKey] ?? null;
  }

  static linkDisplayName(fieldKey) {
    return String(fieldKey ?? "").replace(/^link(\d+)Url$/, "Link $1");
  }

  static isLinkLabelField(appName, entityType, fieldKey) {
    const entityName = String(entityType ?? "").split(".").at(-1);
    return Object.values(LINK_FIELDS_BY_ENTITY[appName]?.[entityName] ?? {}).includes(fieldKey);
  }

  // bindable context. Filters the picker only; an existing binding to one of
  static isHiddenContextEntity(appName, entityType) {
    const entityName = String(entityType ?? "").split(".").at(-1);
    return (HIDDEN_CONTEXT_ENTITIES_BY_APP[appName] ?? []).includes(entityName);
  }


  // → { display, input, displayName?, presentAsDate? } — which palette
  static derivePaletteEntries(fieldKey, fieldDefinition, entityConfig) {
    const none = { display: false, input: false };
    if (!fieldDefinition) {
      return none;
    }
    if (fieldDefinition.noElement) {
      return none;
    }
    if (HIDDEN_FIELDS.includes(fieldKey)) {
      return none;
    }
    // entityType can arrive dotted ("QuizzesPolls.Answer"); key off the
    const entityName = String(entityConfig?.entityType ?? "").split(".").at(-1);
    if ((HIDDEN_FIELDS_BY_ENTITY[entityConfig?.appName]?.[entityName] ?? []).includes(fieldKey)) {
      return none;
    }
    if ((INPUT_ONLY_FIELDS_BY_APP[entityConfig?.appName] ?? []).includes(fieldKey)) {
      const entry = { display: false, input: Boolean(fieldDefinition.exposeAsInput) };
      if (AppPaletteFieldPolicy.isUploadField(entityConfig?.appName, entityName, fieldKey)) {
        entry.uploadInput = true;
        entry.displayName = "File Upload";
      }
      return entry;
    }
    if ((entityConfig?.primaryKeyFields ?? []).includes(fieldKey)) {
      return none;
    }
    if (AppPaletteFieldPolicy.isLinkLabelField(entityConfig?.appName, entityName, fieldKey)) {
      return none;
    }
    if (AppPaletteFieldPolicy.linkLabelFieldFor(entityConfig?.appName, entityName, fieldKey)) {
      return {
        display: true,
        input: false,
        presentAsLink: true,
        displayName: AppPaletteFieldPolicy.linkDisplayName(fieldKey),
      };
    }
    if (fieldKey in DATE_FIELD_LABELS) {
      return {
        display: true,
        input: false,
        displayName: DATE_FIELD_LABELS[fieldKey],
        presentAsDate: true,
      };
    }
    const entityLabels = FIELD_LABELS_BY_ENTITY[entityName] ?? {};
    const entry = { display: true, input: Boolean(fieldDefinition.exposeAsInput) };
    if (fieldKey in entityLabels) {
      entry.displayName = entityLabels[fieldKey];
    }
    return entry;
  }

}

module.exports = AppPaletteFieldPolicy;
